import { ODSCollapse, Icon, ODSInput, ODSGrid } from 'bc-ods';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import type { FC } from 'react';
import { useState, useCallback, useMemo, memo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useFormikContext } from 'formik';
import { cx } from '@emotion/css';

const messages = defineMessages({
  passwordFeedback: {
    id: 'PasswordRequirements.passwordFeedback',
    defaultMessage: `{error, select,
      min {Must be at least 15 characters in length}
      lowercase {Must include at least 1 lowercase letter}
      uppercase {Must include at least 1 uppercase letter}
      digit {Must include at least 1 digit}
      symbol {Must include at least 1 symbol}
      match {Both passwords must match}
      previous {Cannot match your current password}
      other {Required}}`,
  },
  passwordLabel: {
    id: 'PasswordRequirements.newPasswordLabel',
    defaultMessage: '{reset, select, true {New password} other {Password}}',
  },
  confirmPasswordLabel: {
    id: 'PasswordRequirements.confirmNewPasswordLabel',
    defaultMessage: '{reset, select, true {Confirm new password} other {Confirm password}}',
  },
  confirmPasswordFeedback: {
    id: 'PasswordRequirements.confirmPasswordFeedback',
    defaultMessage: '{error, select, match {Passwords do not match} other {Required}}',
  },
});

interface Props {
  /** Hide the password requirements once the user enters a valid password */
  hideRequirementsOnComplete?: boolean;
  /** Is this a password reset form? */
  reset?: boolean;
  passwordName?: string;
  confirmPasswordName?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const PasswordRequirements: FC<Props> = ({
  hideRequirementsOnComplete,
  size,
  className,
  reset,
  passwordName = 'password',
  confirmPasswordName = 'confirmPassword',
}) => {
  const [showHint, setShowHint] = useState(true);
  const [passwordRequirements, setPasswordRequirements] = useState({
    min: false,
    lowercase: false,
    uppercase: false,
    digit: false,
    symbol: false,
  });
  const passwordErrorId = 'password-error';
  const confirmPasswordErrorId = 'confirm-password-error';

  const { errors, touched, isSubmitting, handleChange } = useFormikContext();

  const updatePasswordRequirements = useCallback((password: string) => {
    const newRequirements = {
      min: password.length >= 15,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      digit: /\d/.test(password),
      symbol: /[^a-zA-Z0-9\s]/g.test(password),
    };
    setPasswordRequirements(newRequirements);

    const allRequirementsMet = Object.values(newRequirements).every(Boolean);
    if (allRequirementsMet) {
      setShowHint(false);
    } else {
      setShowHint(true);
    }
  }, []);

  const requirements = useMemo(
    () => (
      <ul
        className="d-flex flex-column gap-1 text-secondary small m-0 ps-0 pt-1"
        id="password-requirements"
      >
        {Object.entries(passwordRequirements).map(([key, met]) => (
          <li key={key} className={cx({ 'text-success': met })} css={{ listStyleType: 'none' }}>
            <Icon name={met ? 'check' : 'times'} className="me-2" fixedWidth />
            <FormattedMessage {...messages.passwordFeedback} values={{ error: key }} />
          </li>
        ))}
      </ul>
    ),
    [passwordRequirements]
  );

  return (
    <ODSGrid columns={1} className={cx('w-100', className)}>
      <ValidatedFormikField
        as={ODSInput}
        name={passwordName}
        type="password"
        data-testid="password"
        aria-describedby={`${errors[passwordName] ? passwordErrorId : ''} password-requirements`}
        errorId={passwordErrorId}
        autoComplete="new-password"
        size={size}
        disabled={isSubmitting}
        label={<FormattedMessage {...messages.passwordLabel} values={{ reset }} />}
        errorMessage={
          touched[passwordName] && (
            <FormattedMessage
              {...messages.passwordFeedback}
              values={{ error: errors[passwordName] }}
            />
          )
        }
        onChange={(e) => {
          updatePasswordRequirements(e.target.value);
          handleChange(e);
        }}
      />
      {hideRequirementsOnComplete ? (
        <ODSCollapse
          isOpen={showHint}
          unmountOnExit={false}
          data-testid="collapse-password-requirements"
        >
          {requirements}
        </ODSCollapse>
      ) : (
        requirements
      )}
      <ValidatedFormikField
        as={ODSInput}
        name={confirmPasswordName}
        type="password"
        data-testid="confirm-password"
        aria-describedby={`${errors[confirmPasswordName] ? confirmPasswordErrorId : ''}`}
        errorId={confirmPasswordErrorId}
        autoComplete="new-password"
        size={size}
        disabled={isSubmitting}
        label={<FormattedMessage {...messages.confirmPasswordLabel} values={{ reset }} />}
        errorMessage={
          touched[confirmPasswordName] && (
            <FormattedMessage
              {...messages.confirmPasswordFeedback}
              values={{ error: errors[confirmPasswordName] }}
            />
          )
        }
      />
    </ODSGrid>
  );
};

PasswordRequirements.displayName = 'PasswordRequirements';
export default memo(PasswordRequirements);
